import { useEffect, useMemo, useState } from 'react'

import { ProductCarousel } from 'slab'
import { type MoneyAmount } from 'types'

import { Details } from './Details'
import { events, SessionContext } from '../shared'
import { Header } from '../shared/Header'
import { type MarketplaceProduct as MarketplaceProductType, type MarketplaceProductVariant } from '../shared/types'
import { getProductBadges } from '../shared/utils'

export const Title = ({ product, classNameExtra = '' }: { product: MarketplaceProductType, classNameExtra?: string }) => (
  <div className={`flex flex-col gap-1 items-start ${classNameExtra}`}>
    <h3 className='body text-gray-500 mb-0'>{product.product_brand}</h3>
    <h1 className='heading-lg text-gray-800 my-0'>{product.name}</h1>
  </div>
)

type MarketplaceProductProps = {
  balance?: MoneyAmount | null;
  product: MarketplaceProductType;
  member_id?: string | null;
  employer_id?: string | null;
  is_authenticated: boolean;
  public_view: boolean;
  public_referral_url: string;
}

export default function MarketplaceProduct ({
  balance,
  product,
  member_id: memberId,
  employer_id: employerId,
  is_authenticated: isAuthenticated,
  public_view: publicView,
  public_referral_url: publicReferralUrl,
}: MarketplaceProductProps) {
  // Note: every product has at least one variant, but single variant products are presented differently
  const [selectedVariant, setSelectedVariant] = useState<MarketplaceProductVariant>(() => product.marketplace_product_variants[0])
  const [startTime, setStartTime] = useState<number>(Date.now())
  const sessionContext = useMemo(() => ({
    id: memberId ?? null,
    employerId: employerId ?? null,
    balance: balance ?? undefined,
    isAuthenticated,
    publicView,
    publicReferralUrl,
  }), [balance, employerId, isAuthenticated, memberId, publicReferralUrl, publicView])

  const badges = useMemo(() => getProductBadges({
    createdAt: selectedVariant.created_at,
    hasDiscount: selectedVariant.discounted_price !== null,
    comingSoon: product.coming_soon,
  }), [selectedVariant, product.coming_soon])

  useEffect(() => {
    setSelectedVariant(product.marketplace_product_variants[0])
  }, [product])

  useEffect(() => {
    const defaultVariant = product.marketplace_product_variants[0]
    setStartTime(Date.now())
    events.emitProductViewEvent(sessionContext, product, defaultVariant)

    const handleBeforeUnload = () => {
      events.emitProductExitEvent(sessionContext, product, Math.round((Date.now() - startTime) / 1000))
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  return (
    <SessionContext.Provider value={sessionContext}>
      <div className='@container/product'>
        <Header product={product} />
        <Title product={product} classNameExtra='flex @xl/product:hidden mt-4' />
        <div className='border border-gray-200 rounded-2xl p-4 md:p-6 shadow-sm bg-white mt-4 min-w-[320px]'>
          <div className='grid grid-cols-1 @2xl/product:grid-cols-2 @3xl/product:grid-cols-[6fr_5fr] gap-6 lg:@2xl/product:gap-10'>
            <div className='min-w-0'>
              <div className='@2xl/product:sticky @2xl/product:top-20 lg:@2xl/product:top-6'>
                <ProductCarousel key={selectedVariant.id} badges={badges}>
                  {selectedVariant.image_urls.map((url, index) => (
                    <ProductCarousel.Item
                      thumbnailUrl={selectedVariant.thumbnail_image_urls[index]}
                      title={selectedVariant.name}
                      url={url}
                      key={index}
                    />
                  ))}
                </ProductCarousel>
              </div>
            </div>
            <Details sessionContext={sessionContext} product={product} selectedVariant={selectedVariant} setSelectedVariant={setSelectedVariant} startTime={startTime} comingSoon={product.coming_soon} />
          </div>
        </div>
      </div>
    </SessionContext.Provider>
  )
}
